import Dialog from "../../General/ConfirmableDialog";
import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { useMsal } from "@azure/msal-react";
import {
  generateTimeOffDocument,
  getTimeOffDocumentErrorMessage,
  TimeOffDocumentType,
} from "../../../api/timeOffDocumentService";

const TimeOffRequestActionButtons = ({ row }: { row: any }) => {
  const { token, role } = useAuth();
  const { accounts } = useMsal();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState({
    approve: false,
    decline: false,
    comment: false,
    ordin: false,
    cerere: false,
  });
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState(
    "Request processed successfully"
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [declineReason, setDeclineReason] = useState("");

  const handleSnackbarClose = () => setStatus("idle");

  const handleApproveOrDecline = async (
    isApprove: boolean,
    reason?: string
  ) => {
    setLoading((prev) => ({
      ...prev,
      approve: isApprove ? true : prev.approve,
      decline: !isApprove ? true : prev.decline,
    }));

    try {
      const endpoint = isApprove
        ? `${apiVariable}/approve/${row.id}`
        : `${apiVariable}/decline/${row.id}`;
      await axios.put(endpoint, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          reviewer: accounts[0]?.name,
          ...(reason?.trim() ? { description: reason.trim() } : {}),
        },
      });
      queryClient.invalidateQueries({ queryKey: ["timeOffRequests"] });
      setSuccessMessage("Request processed successfully");
      setStatus("success");
      if (!isApprove) {
        setIsDeclineDialogOpen(false);
        setDeclineReason("");
      }
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to process request");
    } finally {
      setLoading({
        approve: false,
        decline: false,
        comment: false,
        ordin: false,
        cerere: false,
      });
    }
  };

  const handleAddComment = async () => {
    setLoading((prev) => ({ ...prev, comment: true }));
    try {
      await axios.put(`${apiVariable}/addHrComment/${row.id}`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          comment,
        },
      });
      queryClient.invalidateQueries({ queryKey: ["timeOffRequests"] });
      setSuccessMessage("Comment added successfully");
      setStatus("success");
      setIsDialogOpen(false);
      setComment("");
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to add comment");
    } finally {
      setLoading({
        approve: false,
        decline: false,
        comment: false,
        ordin: false,
        cerere: false,
      });
    }
  };

  const handleGenerateDocument = async (
    documentType: TimeOffDocumentType,
  ) => {
    if (!canGenerateDocuments) return;

    if (!token) {
      setErrorMessage("Your session is not ready. Please try again.");
      setStatus("error");
      return;
    }

    setLoading((prev) => ({ ...prev, [documentType]: true }));

    try {
      await generateTimeOffDocument(row.id.toString(), documentType, token);
      setSuccessMessage(
        `${documentType === "ordin" ? "Ordin" : "Cerere"} generated successfully`,
      );
      setStatus("success");
    } catch (error: unknown) {
      setErrorMessage(
        await getTimeOffDocumentErrorMessage(
          error,
          `Failed to generate ${documentType === "ordin" ? "Ordin" : "Cerere"}`,
        ),
      );
      setStatus("error");
    } finally {
      setLoading((prev) => ({ ...prev, [documentType]: false }));
    }
  };

  const isPending = row.row.status === "pending";
  const isApproved = row.row.status === "approved";
  const isMoldovaEmployee =
    String(row.row.deliveryUnit ?? row.row.employee?.deliveryUnit ?? "")
      .trim()
      .toUpperCase() === "MD";
  const canGenerateDocuments =
    (role?.includes("HR") ?? false) &&
    row.row.policy === "vacation" &&
    isMoldovaEmployee;

  if (
    !isPending &&
    !role?.includes("HeadOfHR") &&
    !canGenerateDocuments
  ) {
    return null;
  }

  return (
    <>
      {isPending && (
        <>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={
              !loading.approve ? () => handleApproveOrDecline(true) : undefined
            }
            sx={{ marginRight: 1 }}
          >
            {loading.approve ? (
              <>
                <CircularProgress size={20} color="inherit" />
              </>
            ) : (
              "Approve"
            )}
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={
              !loading.decline ? () => setIsDeclineDialogOpen(true) : undefined
            }
            sx={{ marginRight: role?.includes("HeadOfHR") ? 1 : 0 }}
          >
            {loading.decline ? (
              <>
                <CircularProgress size={20} color="inherit" />
              </>
            ) : (
              "Decline"
            )}
          </Button>
        </>
      )}
      {role?.includes("HeadOfHR") && (
        <>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setIsDialogOpen(true)}
            disabled={loading.comment}
          >
            {loading.comment ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Add HR Comment"
            )}
          </Button>
          <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
            <DialogTitle>Add HR Comment</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                id="hr-comment"
                type="text"
                fullWidth
                multiline
                minRows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                inputProps={{ maxLength: 500 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleAddComment}
                disabled={!comment.trim() || loading.comment}
              >
                {loading.comment ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {canGenerateDocuments && (
        <>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleGenerateDocument("ordin")}
            disabled={!isApproved || loading.ordin || !token}
            sx={{ ml: 1 }}
            title={
              isApproved
                ? "Generate the approved time-off Ordin"
                : "Ordin can be generated only for an approved request"
            }
          >
            {loading.ordin ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Generate Ordin"
            )}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleGenerateDocument("cerere")}
            disabled={loading.cerere || !token}
            sx={{ ml: 1 }}
            title="Generate the employee's time-off request document"
          >
            {loading.cerere ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Generate Cerere"
            )}
          </Button>
        </>
      )}
      <Dialog
        open={isDeclineDialogOpen}
        onClose={() => setIsDeclineDialogOpen(false)}
        aria-labelledby="decline-request-dialog-title"
      >
        <DialogTitle id="decline-request-dialog-title">
          Decline Request
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to decline this request?
          </DialogContentText>
          <TextField
            margin="dense"
            id="decline-reason"
            label="Reason (optional)"
            type="text"
            fullWidth
            multiline
            minRows={3}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeclineDialogOpen(false)}
            disabled={loading.decline}
          >
            No
          </Button>
          <Button
            color="error"
            onClick={() => handleApproveOrDecline(false, declineReason)}
            disabled={loading.decline}
          >
            {loading.decline ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Yes, Decline"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <StatusSnackbar
        status={status}
        successMessage={successMessage}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

export default TimeOffRequestActionButtons;
